<template>
  <div class="pa-5">
    <div v-if="readyForAction">
      <v-container fluid>
        <v-row class="align-baseline mb-8">
          <v-col cols="6">
            <h1>Packing List</h1>
          </v-col>
          <v-col cols="6" class="d-flex justify-end">
            <img src="@/assets/final-logo-tight.png" alt="Plykea" width="200px" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <div class="item py-2">
              <div class="text-caption text-medium-emphasis">
                Customer Name
                <v-icon :size="14" class="pl-2 no-print" @click="editNameDialog = true">mdi-pencil</v-icon>
              </div>
              <div class="text-body-1">
                {{ order.customerContactDetails.firstName + ' ' + order.customerContactDetails.lastName }}
              </div>
            </div>
            <div class="item py-2">
              <div class="text-caption text-medium-emphasis">Customer Email</div>
              <div class="text-body-1">{{ order.customerEmail }}</div>
            </div>
            <div class="item py-2">
              <div class="text-caption text-medium-emphasis">
                Customer Phone
                <v-icon :size="14" class="pl-2 no-print" @click="editPhoneDialog = true">mdi-pencil</v-icon>
              </div>
              <div class="text-body-1">
                {{ order.customerContactDetails.phone1 }}
              </div>
            </div>
          </v-col>
          <v-col cols="6" class="text-right">
            <div class="item py-2">
              <div class="text-caption text-medium-emphasis">Invoice Number</div>
              <div class="text-body-1">
                {{ order.orderNumber || order.xeroInvoiceNumber }}
              </div>
            </div>
            <div class="item py-2">
              <div class="text-caption text-medium-emphasis">
                Delivery Address
                <v-icon :size="14" class="pl-2 no-print" @click="editAddressDialog = true">mdi-pencil</v-icon>
              </div>
              <div class="text-body-1">
                <div>
                  {{ order.customerContactDetails.deliveryAddressLine1 }}
                </div>
                <div>
                  {{ order.customerContactDetails.deliveryAddressLine2 }}
                </div>
                <div>
                  {{ order.customerContactDetails.deliveryCity }}
                </div>
                <div>
                  {{ order.customerContactDetails.deliveryState }}
                </div>
                <div>
                  {{ order.customerContactDetails.deliveryPostcode }}
                </div>
                <div>
                  {{ order.customerContactDetails.deliveryCountry }}
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- NOTES -->
      <v-container fluid v-if="version.notes">
        <v-row>
          <v-col cols="12">
            <span class="text-h6 mr-2">Notes</span>
            <v-divider class="mt-2"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <p>{{ version.notes }}</p>
          </v-col>
        </v-row>
      </v-container>

      <!-- VISUAL -->
      <v-container fluid v-if="version.customerVisualID">
        <v-row>
          <v-col cols="12">
            <span class="text-h6 mr-2">Visual</span>
            <v-divider class="mt-2"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <CustomerCad :version="version" :quote="quote" :customerView="true" :disableControls="true"> </CustomerCad>
          </v-col>
        </v-row>
      </v-container>

      <!-- FRONTS -->
      <v-container v-for="material in frontMaterials" :key="material.id" fluid class="text-body-2">
        <v-row>
          <v-col cols="12" class="pb-2">
            <span class="text-h6 mr-2" v-if="material.colourCode"
              >{{ material.label }} - {{ material.colourCode }}</span
            >
            <span class="text-h6 mr-2" v-else>{{ material.label }} - {{ material.grainDirection }}</span>
          </v-col>
        </v-row>
        <v-divider class="mt-2"></v-divider>
        <v-row class="my-0">
          <v-col cols="1" class="text-disabled text-caption">Location</v-col>
          <v-col cols="3" class="text-disabled text-caption">Type</v-col>
          <v-col cols="2" class="text-disabled text-caption">Dimension (inch)</v-col>
          <v-col cols="3" class="text-disabled">Handle</v-col>
          <v-spacer></v-spacer>
          <v-col cols="1" class="text-disabled text-caption">Qty</v-col>
          <v-col cols="2" class="text-disabled text-caption text-right">Packed / Packages</v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>
        <v-row v-for="front in byMaterial(version.fronts, material.id)" :key="front.id" class="py-0 my-0">
          <v-col cols="1" class="py-1">{{ front.itemNum }}</v-col>
          <v-col cols="3" class="py-1">{{ productData.getFrontTypeLabel(front.frontType) }}</v-col>
          <v-col cols="2" class="py-1"
            >{{ parseFloat($utils.mmToInch(front.w)) }} x {{ parseFloat($utils.mmToInch(front.h)) }}</v-col
          >
          <v-col cols="3" class="py-1">{{ getHandleName(front.handle) }}</v-col>
          <v-spacer></v-spacer>
          <v-col cols="1" class="py-1">{{ front.qty }}</v-col>
          <v-col cols="2" class="py-1 value d-flex justify-end"
            ><div class="box"></div>
            <div class="ml-2 box"></div
          ></v-col>
        </v-row>

        <v-row
          v-for="coverPanel in byMaterial(version.coverPanels, material.id)"
          :key="coverPanel.id"
          class="py-0 my-0"
        >
          <v-col cols="1">{{ coverPanel.itemNum }}</v-col>
          <v-col cols="3">{{ productData.getCoverPanelTypeLabel(coverPanel.coverPanelType) }}</v-col>
          <v-col cols="2"
            >{{ parseFloat($utils.mmToInch(coverPanel.w)) }} x {{ parseFloat($utils.mmToInch(coverPanel.h)) }}</v-col
          >
          <v-col cols="3" class="d-flex align-center">
            <span v-if="!hasEdgeTreatment(coverPanel)">n/a</span>
            <div v-if="hasEdgeTreatment(coverPanel)" class="edge-treatment-box">
              <div class="label">Edge-banding</div>
              <v-checkbox
                hide-details
                class="top ma-0 pa-0"
                :disabled="true"
                v-model="coverPanel.edges.top"
              ></v-checkbox>
              <v-checkbox
                hide-details
                class="bottom ma-0 pa-0"
                :disabled="true"
                v-model="coverPanel.edges.bottom"
              ></v-checkbox>
              <v-checkbox
                hide-details
                class="edge-left ma-0 pa-0"
                :disabled="true"
                v-model="coverPanel.edges.left"
              ></v-checkbox>
              <v-checkbox
                hide-details
                class="edge-right ma-0 pa-0"
                :disabled="true"
                v-model="coverPanel.edges.right"
              ></v-checkbox>
            </div>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="1">{{ coverPanel.qty }}</v-col>
          <v-col cols="2" class="d-flex justify-end"
            ><div class="box"></div>
            <div class="ml-2 box"></div
          ></v-col>
        </v-row>
      </v-container>

      <!-- SPACER PANELS -->
      <v-container v-if="version.spacerPanels && version.spacerPanels.length > 0" fluid class="text-body-2">
        <v-row>
          <v-col cols="12" class="pb-2">
            <span class="text-h6 mr-2">Spacer Panels</span>
          </v-col>
        </v-row>
        <v-divider class="mt-2"></v-divider>
        <v-row class="my-0">
          <v-col cols="1" class="text-disabled text-caption">Location</v-col>
          <v-col cols="2" class="text-disabled text-caption">Material</v-col>
          <v-col cols="2" class="text-disabled text-caption">Length (inch)</v-col>
          <v-spacer></v-spacer>
          <v-col cols="1" class="text-disabled text-caption">Qty</v-col>
          <v-col cols="2" class="text-disabled text-caption text-right">Packed / Packages</v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>
        <v-row v-for="spacer in version.spacerPanels" :key="spacer.id" class="py-0 my-0">
          <v-col cols="1" class="py-1">{{ spacer.location }}</v-col>
          <v-col cols="2" class="py-1">{{ spacer.material ? spacer.material : '?' }}</v-col>
          <v-col cols="2" class="py-1">{{ parseFloat($utils.mmToInch(spacer.l)) }}</v-col>
          <v-spacer></v-spacer>
          <v-col cols="1" class="py-1">{{ spacer.qty }}</v-col>
          <v-col cols="2" class="py-1 d-flex justify-end"
            ><div class="box"></div>
            <div class="ml-2 box"></div
          ></v-col>
        </v-row>
      </v-container>

      <!-- URTILS -->
      <v-container fluid v-if="version.urtils && version.urtils.length > 0" class="text-body-2">
        <v-row>
          <v-col cols="12" class="pb-2">
            <span class="text-h6 mr-2">Ürtils</span>
          </v-col>
        </v-row>
        <div v-for="urtil in version.urtils" :key="urtil.uid" class="pb-2">
          <v-divider class="my-2"></v-divider>
          <v-row>
            <v-col cols="6" class="text-h8 font-weight-bold text-left">
              {{ '"' + urtil.name.toUpperCase() + '"' }} | {{ urtil.trimming ? 'Trim-Fit' : 'Flat-Pack' }} Ürtil |
              {{
                'W' +
                parseFloat($utils.mmToInch(urtil.w)) +
                '" x ' +
                'H' +
                parseFloat($utils.mmToInch(urtil.h)) +
                '" x ' +
                'D' +
                parseFloat($utils.mmToInch(urtil.d)) +
                '"'
              }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="9" class="text-h8 font-italic text-left"
              >{{ productData.getUrtilType(urtil.type).label }} -
              {{ getMaterialName(urtil.carcass).split('/')[0] + ' Urtil with ' }}
              {{ urtil.compartments + ' Compartments - ' }}
              {{ urtil.lighting ? parseFloat($utils.mmToInch(urtil.lighting)) + '" Lighting - ' : '' }}
              {{ getMaterialName(urtil.back).split('/')[0] + ' Back - ' }}
              {{
                urtil.shelving
                  ? urtil.shelving[0].toUpperCase() + urtil.shelving.slice(1).toLowerCase() + ' Shelves'
                  : ''
              }}
            </v-col>
            <v-col cols="3" class="text-h8 text-right">Parts quantity: {{ getNumUrtilParts(urtil) }}</v-col>
          </v-row>
          <v-divider class="my-2"></v-divider>

          <v-row class="pl-2">
            <v-col cols="9" class="text-disabled">Part Type</v-col>
            <v-col cols="1" class="text-disabled text-right">Qty</v-col>
            <v-col cols="2" class="text-disabled text-right">Packed / Packages</v-col>
          </v-row>

          <!-- FLAT-PACK & TRIM-TO-FIT URTIL CARCASS PARTS -->
          <v-row v-if="urtil.trimming || !urtil.preAssembled" class="pl-2">
            <v-col cols="9">
              Top & Base Ürtil Carcass
              {{ !urtil.trimming ? '(flat-packed)' : '(requires on-site trimming)' }}
            </v-col>
            <v-col cols="1" class="value d-flex justify-end">2</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>
          <v-row v-if="urtil.trimming || !urtil.preAssembled" class="pl-2">
            <v-col cols="9"
              >Left & Right Ürtil Carcass Sides
              {{ !urtil.trimming ? '(with connectors)' : '(with pilot holes)' }}</v-col
            >
            <v-col cols="1" class="value d-flex justify-end">2</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>
          <v-row v-if="(urtil.trimming || !urtil.preAssembled) && urtil.compartments > 1" class="pl-2">
            <v-col cols="9">{{ urtil.compartments - 1 }} Dividers (carcass includes slot)</v-col>
            <v-col cols="1" class="value d-flex justify-end">{{ urtil.compartments - 1 }}</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>

          <!-- URTIL SHELVES -->
          <v-row v-if="urtil.shelving" class="pl-2">
            <v-col cols="9">{{
              getNumShelves(urtil) +
              ' x ' +
              urtil.shelving[0].toUpperCase() +
              urtil.shelving.slice(1).toLowerCase() +
              ' Shelves'
            }}</v-col>
            <v-col cols="1" class="value d-flex justify-end">{{ getNumShelves(urtil) }}</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>

          <!-- URTIL DOORS -->
          <v-row v-if="urtil.material1 && urtil.type !== 'o'" class="pl-2">
            <v-col cols="9"
              >{{ getMaterialName(urtil.material1).split('/')[0] }} Ürtil
              {{ urtil.singleDoorSide ? 'Right ' : 'Left ' }}Door 1 (covering {{ urtil.doorCoverageOne }}
              {{ urtil.doorCoverageOne > 1 ? 'compartments' : 'compartment' }})
            </v-col>
            <v-col cols="1" class="value d-flex justify-end">1</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>
          <v-row v-if="urtil.material2 && urtil.type === 't'" class="pl-2">
            <v-col cols="9"
              >{{ getMaterialName(urtil.material2).split('/')[0] }} Ürtil Door 2 (covering {{ urtil.doorCoverageTwo }}
              {{ urtil.doorCoverageTwo > 1 ? 'compartments' : 'compartment' }})</v-col
            >
            <v-col cols="1" class="value d-flex justify-end">1</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>

          <!-- FLAT-PACK & TRIM-TO-FIT URTIL BACK -->
          <v-row v-if="(urtil.trimming || !urtil.preAssembled) && urtil.back" class="pl-2">
            <v-col cols="9"
              >{{ getMaterialName(urtil.back).split('/')[0] }} Ürtil Back
              {{ !urtil.trimming ? '(flat-packed)' : '(requires on-site trimming)' }}</v-col
            >
            <v-col cols="1" class="value d-flex justify-end">1</v-col>
            <v-col cols="2" class="value d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>
        </div>
      </v-container>

      <!-- WORKTOPS -->
      <v-container fluid v-if="version.worktops && version.worktops.length > 0" class="text-body-2">
        <v-row>
          <v-col cols="12" class="pb-3">
            <span class="text-h6 mr-2">Worktops</span>
          </v-col>
        </v-row>
        <v-divider class="mt-2"></v-divider>
        <v-row class="my-0">
          <v-col cols="2" class="text-disabled text-caption">Material</v-col>
          <v-col cols="8" class="text-disabled text-caption">Section Information</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="text-disabled text-caption text-right">Packed / Packages</v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>
        <template v-for="worktop in version.worktops">
          <v-row v-for="section in worktop.sections" class="py-4" :key="section.uid">
            <v-col
              cols="2"
              class="d-flex text-capitalize justify-start align-center pr-4"
              :style="{ textWrap: 'balance' }"
              >{{ section.customCoreThickness ? section.coreThickness + 'mm' : worktop.thickness + 'mm' }}
              {{ worktop.material }}<br />
              {{ findWorktopColourName(worktop) }} - {{ worktop.colourCode }}<br />({{ worktop.faces }}-sided)</v-col
            >
            <v-col cols="2" class="dimensions px-4">
              <div class="box">
                <div :class="section.dogleg ? 'label-shifted' : 'label'">Dimension (inch)</div>
                <div class="depth v-input text-center text-disabled text-body-2">
                  {{ parseFloat($utils.mmToInch(section.d)) }}
                </div>
                <div
                  :class="
                    section.dogleg
                      ? 'length-shifted v-input text-center text-disabled text-body-2'
                      : 'length v-input text-center text-disabled text-body-2'
                  "
                >
                  {{ parseFloat($utils.mmToInch(section.l)) }}
                </div>
                <div class="innerBox" v-if="section.dogleg">
                  <div class="innerLabel">Dogleg</div>
                  <div class="doglegDepth v-input text-center text-disabled text-body-2">
                    {{ parseFloat($utils.mmToInch(section.di)) }}
                  </div>
                  <div class="doglegLength v-input text-center text-disabled text-body-2">
                    {{ parseFloat($utils.mmToInch(section.li)) }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="2" class="radius px-4">
              <div class="box" v-if="section.radii">
                <div :class="section.dogleg ? 'label-shifted' : 'label'">Radius (mm)</div>
                <div class="v-input bl text-center text-disabled text-body-2">
                  {{ section.radii.bl }}
                </div>
                <div class="v-input br text-center text-disabled text-body-2">
                  {{ section.radii.br }}
                </div>
                <div
                  :class="
                    section.dogleg
                      ? 'v-input fl-dog text-center text-disabled text-body-2'
                      : 'v-input fl text-center text-disabled text-body-2'
                  "
                >
                  {{ section.radii.fl }}
                </div>
                <div class="v-input fr text-center text-disabled text-body-2">
                  {{ section.radii.fr }}
                </div>
                <div v-if="section.dogleg" class="v-input fm-dog text-center text-disabled text-body-2">
                  {{ section.radii.fm }}
                </div>
                <div class="innerBox" v-if="section.dogleg">
                  <div class="innerLabel">Dogleg</div>
                  <div class="v-input i text-center text-disabled text-body-2">
                    {{ section.radii.i }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="2" class="finishing px-4">
              <div class="box" v-if="section.finished">
                <div :class="section.dogleg ? 'label-shifted' : 'label'">Finishing</div>
                <v-checkbox
                  hide-details
                  :class="section.dogleg ? 'front-shifted ma-0 pa-0' : 'front ma-0 pa-0'"
                  :disabled="true"
                  v-model="section.finished.front"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  :class="section.dogleg ? 'left-shifted ma-0 pa-0' : 'edge-left ma-0 pa-0'"
                  :disabled="true"
                  v-model="section.finished.left"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  class="edge-right ma-0 pa-0"
                  :disabled="true"
                  v-model="section.finished.right"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  class="back ma-0 pa-0"
                  :disabled="true"
                  v-model="section.finished.back"
                ></v-checkbox>
                <div class="innerBox" v-if="section.dogleg">
                  <div class="innerLabel">Dogleg</div>
                  <v-checkbox
                    hide-details
                    class="dogLong ma-0 pa-0"
                    :disabled="true"
                    v-model="section.finished.dogLong"
                  ></v-checkbox>
                  <v-checkbox
                    hide-details
                    class="dogShort ma-0 pa-0"
                    :disabled="true"
                    v-model="section.finished.dogShort"
                  ></v-checkbox>
                </div>
              </div>
            </v-col>
            <v-col cols="2" class="edge-banding px-4">
              <div class="box" v-if="section.edgeBanded">
                <div :class="section.dogleg ? 'label-shifted' : 'label'">Edge-banding</div>
                <v-checkbox
                  hide-details
                  :class="section.dogleg ? 'front-shifted ma-0 pa-0' : 'front ma-0 pa-0'"
                  :disabled="true"
                  v-model="section.edgeBanded.front"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  :class="section.dogleg ? 'left-shifted ma-0 pa-0' : 'edge-left ma-0 pa-0'"
                  :disabled="true"
                  v-model="section.edgeBanded.left"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  class="edge-right ma-0 pa-0"
                  :disabled="true"
                  v-model="section.edgeBanded.right"
                ></v-checkbox>
                <v-checkbox
                  hide-details
                  class="back ma-0 pa-0"
                  :disabled="true"
                  v-model="section.edgeBanded.back"
                ></v-checkbox>
                <div class="innerBox" v-if="section.dogleg">
                  <div class="innerLabel">Dogleg</div>
                  <v-checkbox
                    hide-details
                    class="dogLong ma-0 pa-0"
                    :disabled="true"
                    v-model="section.edgeBanded.dogLong"
                  ></v-checkbox>
                  <v-checkbox
                    hide-details
                    class="dogShort ma-0 pa-0"
                    :disabled="true"
                    v-model="section.edgeBanded.dogShort"
                  ></v-checkbox>
                </div>
              </div>
            </v-col>
            <v-col cols="2" class="d-flex justify-end align-center">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </v-row>
        </template>
      </v-container>

      <!-- ADDITIONAL ITEMS -->
      <v-container fluid class="text-body-2">
        <v-row>
          <v-col cols="12" class="pb-1">
            <span class="text-h6 mr-2">Additional Items</span>
          </v-col>
        </v-row>
        <v-divider class="mt-2"></v-divider>
        <v-row class="my-0 justify-space-between">
          <v-col cols="6" class="text-disabled text-caption">Item</v-col>
          <v-col cols="2" class="text-disabled text-caption text-right">Packed / Packages</v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>
        <v-row v-for="item in version.additionalItems" :key="item.id" class="py-0 my-0 justify-space-between">
          <v-col cols="6">{{ item.description }}</v-col>
          <v-col cols="2" class="d-flex justify-end"
            ><div class="box"></div>
            <div class="ml-2 box"></div
          ></v-col>
        </v-row>
        <div v-if="numRequiredHandles.length > 0">
          <v-row v-for="item in numRequiredHandles" :key="item.id" class="py-0 my-0">
            <v-col cols="6">{{ item.count }} x {{ item.name }}</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end"
              ><div class="box"></div>
              <div class="ml-2 box"></div
            ></v-col>
          </v-row>
        </div>

        <v-row v-if="numPlinthClips > 0" class="py-0 my-0">
          <v-col cols="6">{{ numPlinthClips }} x Plinth Clips</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end"
            ><div class="box"></div>
            <div class="ml-2 box"></div
          ></v-col>
        </v-row>

        <v-row v-if="numPlinthClips > 0" class="py-0 my-0">
          <v-col cols="6">{{ numPlinthClips * 2 }} x Plinth Screws</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>
        </v-row>

        <v-row v-if="numRequiredJoiningBrackets > 0" class="py-0 my-0">
          <v-col cols="6">{{ numRequiredJoiningBrackets }} x Joining Brackets</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>
        </v-row>

        <!-- URTIL ADDITIONAL ITEMS -->
        <v-row v-if="version.urtils && version.urtils.length > 0" class="py-0 my-0">
          <!-- INSTALLATION INSTRUCTIONS (Flat-Pack and Trim-Fit only, no pre-assembled) -->
          <template v-if="version.urtils.some((urtil) => !urtil.preAssembled)">
            <v-col cols="6">Flat-Pack Ürtil installation</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <template v-if="version.urtils.some((urtil) => urtil.trimming)">
            <v-col cols="6">Trim-Fit Ürtil installation</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <!-- hex key for flat-pack (urtil only)-->
          <template
            v-if="version.urtils.filter((urtil) => !urtil.preAssembled).length > 0 && version.urtils.length > 0"
          >
            <v-col cols="6">Hex key for Flat-Pack Assembly</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <!-- lighting cable cover -->
          <template
            v-if="version.urtils.some((urtil) => urtil.lighting) || (shelfWithLighting && shelfWithLighting.length > 0)"
          >
            <v-col cols="6">Lighting Cable Covers</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <!-- BATON -->
          <v-col cols="12" v-if="version.urtils && version.urtils.length > 0">
            <v-row v-for="(urtil, index) in version.urtils" :key="index">
              <v-col cols="6">{{ parseFloat($utils.mmToInch(urtil.w - 30)) }}" Ürtil mounting baton</v-col>
              <v-spacer></v-spacer>
              <v-col cols="2" class="d-flex justify-end">
                <div class="box"></div>
                <div class="ml-2 box"></div>
              </v-col>
            </v-row>
          </v-col>

          <!-- SCREWS + PLUGS + CUPS -->
          <!-- wall-baton screws + plug -->
          <v-col cols="6">{{ numRequiredBatonScrews }} of #10 x 3" screws</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>

          <v-col cols="6">{{ numRequiredBatonScrews }} Wall Plugs</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>

          <v-col cols="6">{{ numRequiredMetalSpacers }} 7/16" washer (.459" ID x .922" OD x .08" thickness)</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>

          <v-col cols="6">{{ numRequiredBatonScrews }} of #8 x 1" baton screws</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>

          <v-col cols="6">{{ numRequiredBatonScrews }} Brass screw cups (grommet for #8)</v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="d-flex justify-end">
            <div class="box"></div>
            <div class="ml-2 box"></div>
          </v-col>

          <template v-if="numRequiredBackScrews > 0">
            <v-col cols="6"> {{ numRequiredBackScrews }} of #5 x 1" back screws </v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <template v-if="numRequiredJointScrews > 0">
            <v-col cols="6">{{ numRequiredJointScrews }} of #8 x 2" joint screws</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>

          <template v-if="numRequiredShelvingPins > 0">
            <v-col cols="6">{{ numRequiredShelvingPins }} Shelving pins</v-col>
            <v-spacer></v-spacer>
            <v-col cols="2" class="d-flex justify-end">
              <div class="box"></div>
              <div class="ml-2 box"></div>
            </v-col>
          </template>
        </v-row>
      </v-container>

      <v-divider class="my-6"></v-divider>

      <v-container fluid class="footer">
        <v-row>
          <v-col cols="3">
            <div class="text-caption text-disabled mb-2">Packed By</div>
            <div class="longBox mb-3"></div>
            <div class="text-caption text-disabled mb-2">Date</div>
            <div class="longBox mb-3"></div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption text-disabled mb-2">Collected By</div>
            <div class="longBox mb-3"></div>
            <div class="text-caption text-disabled mb-2">Date</div>
            <div class="longBox mb-3"></div>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="2" class="text-right">
            <div class="text-caption text-disabled mb-2">Total Packages</div>
            <div class="box" :style="{ 'margin-left': 'auto' }"></div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-dialog v-if="readyForAction" v-model="editAddressDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Edit Delivery Address</v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form" validate-on="blur">
            <v-text-field
              v-model="order.customerContactDetails.deliveryAddressLine1"
              label="Delivery Address Line 1"
            ></v-text-field>
            <v-text-field
              v-model="order.customerContactDetails.deliveryAddressLine2"
              label="Delivery Address Line 2"
            ></v-text-field>
            <v-text-field v-model="order.customerContactDetails.deliveryCity" label="City"> </v-text-field>
            <v-autocomplete
              disabled
              v-model="order.customerContactDetails.deliveryState"
              :items="$utils.usStateCodes"
              label="State"
              :rules="[$rules.required('Delivery State')]"
            >
            </v-autocomplete>
            <v-text-field
              v-model="order.customerContactDetails.deliveryPostcode"
              label="Delivery Zip code"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="editAddressDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="accent" variant="flat" @click="updateCustomerContactDetails">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="readyForAction" v-model="editNameDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Edit Customer Name</v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form">
            <v-text-field v-model="order.customerContactDetails.firstName" label="First Name"></v-text-field>
            <v-text-field v-model="order.customerContactDetails.lastName" label="Last Name"></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="editNameDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="accent" variant="flat" @click="updateCustomerContactDetails">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="readyForAction" v-model="editPhoneDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Edit Customer Phone</v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form">
            <v-text-field v-model="order.customerContactDetails.phone1" label="Customer Phone"></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="editPhoneDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="accent" variant="flat" @click="updateCustomerContactDetails">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :model-value="!readyForAction" class="align-center justify-center">
      <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import productData from '@/js/productData'
import CustomerCad from '@/components/CustomerCad'
import cadMethods from '@/js/cad/CadMethods.js'
import { db } from '@/js/db'
import { doc, getDoc, updateDoc } from 'firebase/firestore'

export default {
  name: 'LucidProductionView',
  props: ['orderID'],
  components: { CustomerCad },
  data() {
    return {
      productData: productData,
      version: null,
      order: null,
      quote: null,
      readyForAction: false,
      editNameDialog: false,
      editAddressDialog: false,
      editPhoneDialog: false
    }
  },
  async created() {
    const orderSnap = await getDoc(doc(db, 'orders', this.orderID))
    this.order = orderSnap.data()
    const quoteSnap = await getDoc(doc(db, 'quotes', orderSnap.data().quoteID))
    this.quote = quoteSnap.data()
    this.version = this.$utils.getVersionByID(quoteSnap.data().versions, orderSnap.data().versionID)
    this.readyForAction = true
  },
  methods: {
    byMaterial(items, materialId) {
      return (items || []).filter((item) => item.material == materialId)
    },
    async updateCustomerContactDetails() {
      this.editNameDialog = false
      this.editAddressDialog = false
      this.editPhoneDialog = false

      updateDoc(doc(db, 'orders', this.orderID), {
        customerContactDetails: this.order.customerContactDetails
      })

      this.$store.commit('openSnackbar', {
        snackbarText: 'Customer details updated'
      })
    },
    getHandleName(id) {
      for (var i = 0; i < this.version.handles.length; i++) {
        if (this.version.handles[i].id == id) {
          return this.version.handles[i].label
        }
      }
    },
    getHandleType(id) {
      for (var i = 0; i < this.version.handles.length; i++) {
        if (this.version.handles[i].id == id) {
          return this.version.handles[i]
        }
      }
    },
    getMaterialName(code) {
      for (var i = 0; i < this.version.materials.length; i++) {
        if (code == this.version.materials[i].id) {
          var m = this.version.materials[i]
          return m.colourCode ? m.label + ' / ' + m.colourCode : m.label
        }
      }
    },
    getNumShelves(urtil) {
      if (!urtil.shelving) return 0
      return urtil.shelving !== 'fixed' ? urtil.shelvingOptions.qty * urtil.compartments : urtil.shelvingOptions.qty
    },
    getNumUrtilParts(urtil) {
      let numParts = 0

      // Only count flat-pack/trim-to-fit parts (US urtils are never preAssembled)
      if (urtil.trimming || !urtil.preAssembled) {
        // carcass parts: top, base, left, right = 4
        numParts += 4
        // dividers
        if (urtil.compartments > 1) {
          numParts += urtil.compartments - 1
        }
        // back
        if (urtil.back) {
          numParts += 1
        }
      }

      // urtil doors
      if (urtil.type === 's' && urtil.material1) {
        numParts++
      } else if (urtil.type === 't' && urtil.material2) {
        numParts += 2
      }

      // shelves
      if (urtil.shelvingOptions) {
        const numShelves = this.getNumShelves(urtil)
        numParts += numShelves
      }

      return numParts
    },
    hasEdgeTreatment(coverPanel) {
      if (!coverPanel.edges || !coverPanel.edgeMode) return false
      const edges = coverPanel.edges
      return edges.top || edges.bottom || edges.left || edges.right
    },
    findWorktopColourName(worktop) {
      if (!this.version?.worktops) return null

      let worktopInfo
      if (!this.$store) return null
      if (this.$store.state.materials) {
        const colouredMaterials = this.$store.state.materials?.filter((material) => material.colours)
        const colours = colouredMaterials?.map((colouredMaterial) => colouredMaterial.colours)
        for (const materialSet of colours) {
          for (const colourInfo of materialSet) {
            if (colourInfo.value === worktop.colourCode) {
              worktopInfo = colourInfo
            }
          }
        }
        return worktopInfo?.text || null
      } else {
        return null
      }
    }
  },
  computed: {
    frontMaterials() {
      return (this.version.materials || []).filter(
        (material) => this.version.pricing.fronts[material.id] && this.version.pricing.fronts[material.id].total > 0
      )
    },
    numRequiredHandles() {
      var a = []
      let o, f, ii

      for (var i = 0; i < this.version.handles.length; i++) {
        var h = this.version.handles[i]
        if (h.value.split('-')[0] == 'edge') {
          o = {
            name: h.label,
            count: 0
          }

          for (ii = 0; ii < this.version.fronts.length; ii++) {
            f = this.version.fronts[ii]

            if (f.handle == h.id) {
              if (f.frontType == 'lhd-2-p-cnr' || f.frontType == 'rhd-2-p-cnr') {
                o.count += f.qty / 2
              } else {
                o.count += f.qty
              }
            }
          }

          a.push(o)
        } else if (h.value.split('-')[0] == 'dpull') {
          o = {
            name: h.label,
            count: 0
          }

          for (ii = 0; ii < this.version.fronts.length; ii++) {
            f = this.version.fronts[ii]

            if (f.handle == h.id) {
              if (f.frontType == 'lhd-2-p-cnr' || f.frontType == 'rhd-2-p-cnr') {
                o.count += f.qty / 2
              } else {
                o.count += f.qty
              }
            }
          }

          a.push(o)
        }
      }
      return a
    },
    numPlinthClips() {
      var c = 0
      for (var i = 0; i < this.version.coverPanels.length; i++) {
        if (this.version.coverPanels[i].coverPanelType == 'plinth')
          c += productData.plinthClipsPerPlinth * this.version.coverPanels[i].qty
      }
      return c
    },
    numRequiredJoiningBrackets() {
      let count = 0
      for (const front of this.version.fronts) {
        let handleType = this.getHandleType(front.handle)
        if (
          handleType.value != 'j' &&
          ['mid', 'bot'].includes(front.joiningPos) &&
          !(front.joiningPos && handleType.value == 'lrgSemiGrab' && front.w <= 647)
        ) {
          count += 2
        }
      }
      return count
    },
    shelfWithLighting() {
      const shelfArr = this.version.coverPanels.filter((cp) => cp.coverPanelType === 'shelf')
      return shelfArr.filter((shelf) => shelf.lighting)
    },
    numRequiredShelvingPins() {
      let num = 0

      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          if (urtil.shelving === 'adjustable' || urtil.shelving === 'blank') {
            num += 4 * urtil.shelvingOptions.qty * urtil.compartments
          }
        }
      }

      return num
    },
    numRequiredJointScrews() {
      let num = 0

      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          if (urtil.trimming) {
            const pilot = cadMethods.calculateJoint(urtil.d)
            num += pilot.pilotCount * 4 // 4 joints per urtil
          }
        }
      }

      return num
    },
    numRequiredBackScrews() {
      // calculate back screws for trim-urtil + flat-pack (US urtils are never preAssembled)
      let num = 0

      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          let drillSpacing = 150
          if (urtil.trimming || !urtil.preAssembled) {
            num += Math.ceil(urtil.w / drillSpacing) * 2
            num += (urtil.h / drillSpacing) * 2 >= 4 ? Math.ceil(urtil.h / drillSpacing) * 2 : 4
            if (urtil.compartments > 1) {
              num += 3 * (urtil.compartments - 1) // 3 screws per compartment
            }
          }
        }
      }

      return num
    },
    numRequiredBatonScrews() {
      // calculate baton screws for urtil
      let num = 0
      let drillSpacing = 250
      let drillInset = 50

      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          num += Math.ceil((urtil.w - drillInset * 2) / drillSpacing)
        }
      }

      return num
    },
    numRequiredMetalSpacers() {
      // calculate metal spacers for urtil
      let num = 0
      let drillSpacing = 250
      let drillInset = 50

      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          num += Math.ceil((urtil.w - drillInset * 2) / drillSpacing) * 2
        }
      }

      return num
    }
  }
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 3.125rem;
}

.finishing,
.edge-banding,
.radius,
.dimensions {
  .box {
    position: relative;
    width: 100%;
    height: 70px;
    border: solid grey 1px;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-input {
      position: absolute;
      margin: 0;
      min-width: 25px;
      background: white;
      align-items: center;
      justify-content: center;
    }

    .back {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .front {
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
    }
    .edge-left {
      left: 0%;
      transform: translateX(-50%);
    }
    .edge-right {
      right: 0%;
      transform: translateX(50%);
    }

    .bl {
      top: 0;
      left: 0;
      transform: translate(-50%, -50%);
    }
    .br {
      top: 0;
      right: 0;
      transform: translate(50%, -50%);
    }
    .fr {
      bottom: 0;
      right: 0;
      transform: translate(50%, 50%);
    }
    .fl {
      bottom: 0;
      left: 0;
      transform: translate(-50%, 50%);
    }
    .fl-dog {
      top: 25%;
      left: 0;
      transform: translate(-50%, 50%);
      z-index: 1;
    }
    .fm-dog {
      bottom: 0%;
      left: 50%;
      transform: translate(-50%, 50%);
      z-index: 1;
    }
    .depth {
      right: 0%;
      transform: translateX(50%);
    }
    .length {
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 50%);
    }
    .length-shifted {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .label-shifted {
      font-size: 9px;
      color: #666;
      text-align: center;
      margin-left: 50%;
    }
    .front-shifted {
      bottom: 0;
      left: 75%;
      transform: translate(-50%, 50%);
    }
    .left-shifted {
      bottom: 50%;
      left: 0%;
      transform: translateX(-50%);
    }
    .innerBox {
      position: absolute;
      width: 50%;
      height: 50%;
      border-right: solid grey 1px;
      border-top: solid grey 1px;
      bottom: 0;
      transform: translateX(-50%);

      .innerLabel {
        margin-top: 5%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
      }
    }
    .dogLong {
      top: 0%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .dogShort {
      top: 50%;
      right: 0%;
      transform: translate(50%, -50%);
    }
    .i {
      top: 0;
      right: 0;
      transform: translate(50%, -50%);
    }
    .doglegDepth {
      top: 50%;
      right: 0;
      transform: translate(50%, -50%);
      z-index: 1;
    }
    .doglegLength {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
    }
  }
}

.footer {
  .longBox {
    width: 100%;
    height: 25px;
    border: solid 1px black;
  }
}

// Edge Treatment Visual
.edge-treatment-box {
  position: relative;
  width: 50%;
  height: 70px;
  border: solid grey 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  .label {
    font-size: 9px;
    color: #666;
    text-align: center;
  }

  .v-input {
    position: absolute;
    margin: 0;
    min-width: 20px;
    background: white;
    align-items: center;
    justify-content: center;

    // 🔴 V2 name; V3's is `.v-selection-control__input`. `margin-right` is dropped with the
    // rename: V3's selection control has no margin of its own to cancel.
    :deep(.v-selection-control__input) {
      transform: scale(0.7);
    }
  }

  .top {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .bottom {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  .edge-left {
    left: 0%;
    transform: translateX(-50%);
  }

  .edge-right {
    right: 0%;
    transform: translateX(50%);
  }
}
</style>
